import { API, authHeaders, jsonAuthHeaders } from "./config";

const SW_URL = `${process.env.PUBLIC_URL || ""}/sw.js`;

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    return null;
  }

  return navigator.serviceWorker.register(SW_URL, { scope: "/" });
}

export async function getLocalPushSubscription() {
  if (!isPushSupported()) {
    return null;
  }

  try {
    const registration = await registerServiceWorker();
    if (!registration) {
      return null;
    }

    return registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function fetchPushStatus(profesionalId) {
  const localSubscription = await getLocalPushSubscription();
  const params = new URLSearchParams();

  if (profesionalId) {
    params.set("profesional_id", String(profesionalId));
  }

  if (localSubscription?.endpoint) {
    params.set("endpoint", localSubscription.endpoint);
  }

  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(`${API}/push/status${query}`, { headers: authHeaders() });
  const json = await res.json();

  if (!res.ok || !json.success) {
    throw new Error(json.message || "No se pudo consultar el estado de push.");
  }

  return json.data;
}

export async function fetchVapidPublicKey() {
  const res = await fetch(`${API}/push/vapid-public-key`, { headers: authHeaders() });
  const json = await res.json();

  if (!res.ok || !json.success) {
    throw new Error(json.message || "Push no configurado en el servidor.");
  }

  return json.publicKey;
}

export async function subscribeToPush(profesionalId) {
  if (!isPushSupported()) {
    throw new Error("Este navegador no soporta notificaciones push.");
  }

  const registration = await registerServiceWorker();
  if (!registration) {
    throw new Error("No se pudo registrar el service worker.");
  }

  const publicKey = await fetchVapidPublicKey();
  const permission = await Notification.requestPermission();

  if (permission !== "granted") {
    throw new Error("Necesitamos permiso para enviarte notificaciones.");
  }

  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const subscriptionPayload = subscription.toJSON();
  if (subscription.contentEncoding) {
    subscriptionPayload.contentEncoding = subscription.contentEncoding;
  }

  const res = await fetch(`${API}/push/subscribe`, {
    method: "POST",
    headers: jsonAuthHeaders(),
    body: JSON.stringify({
      profesional_id: profesionalId,
      subscription: subscriptionPayload,
    }),
  });

  const json = await res.json();

  if (!res.ok || !json.success) {
    throw new Error(json.message || "No se pudo activar las notificaciones.");
  }

  return json;
}

export async function unsubscribeFromPush() {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    return;
  }

  await fetch(`${API}/push/unsubscribe`, {
    method: "DELETE",
    headers: jsonAuthHeaders(),
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  });

  await subscription.unsubscribe();
}

export function getNotificationPermission() {
  if (!("Notification" in window)) {
    return "unsupported";
  }

  return Notification.permission;
}

export function isIosDevice() {
  if (typeof navigator === "undefined") {
    return false;
  }

  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function isStandalonePwa() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.matchMedia("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
}
